/**
 * CottonGradeMasterPage — Phase 2 Sprint B Phase 3
 * ─────────────────────────────────────────────────────────────────────────────
 * CRUD page for the 7 cotton grades (E1, E2, A, B, C, D, R) defined in Sec 1
 * of the cotton mixing standards spec. Mill admins can tune HVI ranges per
 * grade or add custom grades for export markets.
 *
 * Layout:
 *   - Header: Backfill button + active filter
 *   - Table: each grade as one row, color-coded chip on left, ranges in
 *     compact format ("UHML 31-34mm" "MIC 3.6-4.3" etc.)
 *   - Edit drawer for individual grade tuning
 *
 * Spec source: Cotton mixing standards master, Sec 1.
 */
import { useState } from 'react'
import {
  App, Alert, Button, Card, Col, Drawer, Form, Input, InputNumber, Row,
  Space, Switch, Table, Tag, Tooltip, Typography,
} from 'antd'
import {
  EditOutlined, ReloadOutlined, ExperimentOutlined, InfoCircleOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import PageHeader from '@/shared/components/PageHeader'
import { cottonGradeApi } from '@/features/baleManagement/api'

const { Text } = Typography

// ── Types ────────────────────────────────────────────────────────────────────
type CottonGrade = {
  id: string
  grade_code: string
  grade_name: string
  priority: number
  uhml_mm_min: number | null
  uhml_mm_max: number | null
  strength_min: number | null
  strength_max: number | null
  mic_min: number | null
  mic_max: number | null
  ui_min: number | null
  sfi_max: number | null
  usage_text: string | null
  description: string | null
  is_restricted: boolean
  is_active: boolean
  created_at: string
}

type BackfillResult = {
  E1: number; E2: number; A: number; B: number; C: number; D: number;
  ungraded: number; total: number;
}

// ── Grade color palette (matches spec mental model) ──────────────────────────
const GRADE_COLOR: Record<string, string> = {
  E1: '#722ed1',  // purple — premium
  E2: '#9254de',
  A:  '#52c41a',  // green — high
  B:  '#1677ff',  // blue — good
  C:  '#faad14',  // orange — medium
  D:  '#fa8c16',  // darker orange — economy
  R:  '#8c8c8c',  // gray — restricted
}

function formatRange(min: number | null, max: number | null, unit = ''): string {
  if (min == null && max == null) return '—'
  if (min == null) return `≤ ${max}${unit}`
  if (max == null) return `≥ ${min}${unit}`
  return `${min}–${max}${unit}`
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function CottonGradeMasterPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [editing, setEditing] = useState<CottonGrade | null>(null)
  const [showInactive, setShowInactive] = useState(false)
  const [form] = Form.useForm()

  const gradesQ = useQuery<CottonGrade[]>({
    queryKey: ['cotton-grades', showInactive],
    queryFn: () => cottonGradeApi.list(!showInactive),
  })

  const update = useMutation({
    mutationFn: (data: { id: string; payload: Record<string, unknown> }) =>
      cottonGradeApi.update(data.id, data.payload),
    onSuccess: () => {
      message.success('Grade updated')
      qc.invalidateQueries({ queryKey: ['cotton-grades'] })
      setEditing(null)
    },
    onError: (e: Error) => message.error(e.message),
  })

  const backfill = useMutation<BackfillResult>({
    mutationFn: () => cottonGradeApi.backfill(),
    onSuccess: (result) => {
      const total = result.total
      const graded = total - result.ungraded
      message.success(
        `Backfill complete: ${graded}/${total} bales graded ` +
        `(E1:${result.E1} E2:${result.E2} A:${result.A} B:${result.B} ` +
        `C:${result.C} D:${result.D} ungraded:${result.ungraded})`,
        8,
      )
      qc.invalidateQueries({ queryKey: ['floor-map'] })
    },
    onError: (e: Error) => message.error(e.message),
  })

  const startEdit = (g: CottonGrade) => {
    setEditing(g)
    form.setFieldsValue(g)
  }

  const submitEdit = () => {
    form.validateFields().then(values => {
      if (!editing) return
      update.mutate({ id: editing.id, payload: values })
    })
  }

  return (
    <div style={{ padding: 16 }}>
      <PageHeader
        title="Cotton Grade Master"
        subtitle="E1/E2/A/B/C/D/R — HVI ranges that classify each bale"
        extra={
          <Space>
            <Space size={4}>
              <Switch
                checked={showInactive}
                onChange={setShowInactive}
                size="small"
              />
              <Text style={{ fontSize: 12 }}>Show inactive</Text>
            </Space>
            <Button
              icon={<ReloadOutlined spin={backfill.isPending} />}
              onClick={() => backfill.mutate()}
              loading={backfill.isPending}
            >
              Re-grade all bales
            </Button>
          </Space>
        }
      />

      <Alert
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        style={{ marginBottom: 16 }}
        message="How grades work"
        description={
          <span style={{ fontSize: 13 }}>
            The resolver checks each grade in <strong>priority order</strong>
            {' '}(E1 first, D last) and assigns the first one whose UHML / Strength
            / Mic / UI / SFI ranges all match the bale&apos;s HVI. <strong>R is
            never auto-assigned</strong> — it&apos;s a manual tag for old stock or
            marginal bales, capped per count via Sec 5 Rule 2.
          </span>
        }
      />

      <Card styles={{ body: { padding: 0 } }}>
        <Table
          rowKey="id"
          dataSource={gradesQ.data || []}
          loading={gradesQ.isLoading}
          pagination={false}
          columns={[
            {
              title: 'Grade', dataIndex: 'grade_code', width: 100,
              render: (code: string, r: CottonGrade) => (
                <Space>
                  <Tag
                    color={GRADE_COLOR[code] || 'default'}
                    style={{ fontSize: 14, padding: '4px 10px',
                             color: '#fff', borderColor: 'transparent' }}
                  >
                    {code}
                  </Tag>
                  {r.is_restricted && <Tag color="red">restricted</Tag>}
                </Space>
              ),
            },
            {
              title: 'Name', dataIndex: 'grade_name', ellipsis: true,
              render: (name: string, r: CottonGrade) => (
                <Space direction="vertical" size={0}>
                  <Text strong>{name}</Text>
                  {r.usage_text && (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {r.usage_text}
                    </Text>
                  )}
                </Space>
              ),
            },
            {
              title: 'UHML', width: 110, align: 'right',
              render: (_: unknown, r: CottonGrade) => (
                <Text>{formatRange(r.uhml_mm_min, r.uhml_mm_max, 'mm')}</Text>
              ),
            },
            {
              title: 'Strength', width: 110, align: 'right',
              render: (_: unknown, r: CottonGrade) => (
                <Text>{formatRange(r.strength_min, r.strength_max, ' GPT')}</Text>
              ),
            },
            {
              title: 'MIC', width: 90, align: 'right',
              render: (_: unknown, r: CottonGrade) => (
                <Text>{formatRange(r.mic_min, r.mic_max)}</Text>
              ),
            },
            {
              title: 'UI ≥', width: 70, align: 'right',
              render: (_: unknown, r: CottonGrade) =>
                r.ui_min != null ? <Text>{r.ui_min}%</Text> : <Text type="secondary">—</Text>,
            },
            {
              title: 'SFI ≤', width: 70, align: 'right',
              render: (_: unknown, r: CottonGrade) =>
                r.sfi_max != null ? <Text>{r.sfi_max}%</Text> : <Text type="secondary">—</Text>,
            },
            {
              title: 'Active', dataIndex: 'is_active', width: 80, align: 'center',
              render: (v: boolean) =>
                v ? <Tag color="green">active</Tag> : <Tag>inactive</Tag>,
            },
            {
              title: '', width: 60, align: 'center',
              render: (_: unknown, r: CottonGrade) => (
                <Tooltip title="Edit ranges">
                  <Button
                    type="text" icon={<EditOutlined />}
                    onClick={() => startEdit(r)}
                  />
                </Tooltip>
              ),
            },
          ]}
        />
      </Card>

      <Drawer
        title={
          editing ? (
            <Space>
              <ExperimentOutlined />
              <Text>Edit grade <Tag color={GRADE_COLOR[editing.grade_code]}
                style={{ color: '#fff' }}>{editing.grade_code}</Tag></Text>
            </Space>
          ) : 'Edit grade'
        }
        open={editing !== null}
        onClose={() => setEditing(null)}
        width={520}
        footer={
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => setEditing(null)}>Cancel</Button>
            <Button type="primary" onClick={submitEdit} loading={update.isPending}>
              Save
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Grade name" name="grade_name" rules={[{ required: true }]}>
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item label="Usage hint" name="usage_text">
            <Input maxLength={200} placeholder="e.g. 30s-40s, good 24s" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="UHML min (mm)" name="uhml_mm_min">
                <InputNumber min={0} max={50} step={0.5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="UHML max (mm)" name="uhml_mm_max">
                <InputNumber min={0} max={50} step={0.5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Strength min (g/tex)" name="strength_min">
                <InputNumber min={0} max={50} step={0.5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Strength max (g/tex)" name="strength_max">
                <InputNumber min={0} max={50} step={0.5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="MIC min" name="mic_min">
                <InputNumber min={2} max={6} step={0.1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="MIC max" name="mic_max">
                <InputNumber min={2} max={6} step={0.1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="UI min (%)" name="ui_min">
                <InputNumber min={50} max={100} step={0.5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="SFI max (%)" name="sfi_max">
                <InputNumber min={0} max={20} step={0.5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item label="Priority" name="priority"
            extra="Lower runs first. Don't change unless you know what you're doing.">
            <InputNumber min={1} max={999} style={{ width: 120 }} />
          </Form.Item>
          <Form.Item label="Active" name="is_active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  )
}
